/**
 * The LogoNuri brand-kit source for the image picker (LN-5).
 *
 * KITS ARE THE CATEGORY, ASSETS ARE THE GRID. The picker already has a category
 * `<select>` — shown only for the Local tab until now — so two-level browse
 * comes for free with no new chrome, and the selection persists per adapter.
 * (Per ADAPTER, never one shared key: a LogoNuri slug carried into the Local
 * tab would silently filter it to nothing.)
 *
 * `needsKey: false` IS THE KEY DECISION. The picker calls `getApiKey()`
 * SYNCHRONOUSLY inside `switchTab`, and "is this account linked" is an async
 * server fact. Rather than bolt an async `prepare()` hook onto every adapter,
 * `search()` throws a coded error and the picker's EXISTING
 * `handleSearchError → showGatePrompt` path renders the gate — reusing the
 * machine that already exists for `NO_KEY` instead of inventing a second one.
 *
 * THE INVARIANT, and the reason this file has no `getSourceUrl`. The picker's
 * `handlePick` has a CORS fallback that, when an adapter exposes that hook,
 * applies the RAW URL into the project. For LogoNuri that would hot-link
 * logonuri.com into an EXPORTED CUSTOMER SITE, which breaks the moment the
 * share is revoked — months later, with no symptom until then. Its absence is
 * load-bearing and is asserted by a test.
 *
 * Thumbnails are the public LogoNuri asset URLs, because an `<img src>` cannot
 * carry the PageStitch session cross-origin and those routes are public with
 * CORS `*`. They are built HERE from a constant origin plus a validated slug
 * plus a filename taken from validated metadata — never from an upstream-
 * supplied `url` string, which would be a stored open redirect waiting to
 * happen. Bytes for anything that lands in a project always come through the
 * authenticated proxy.
 */

import {
  LOGONURI_ORIGIN,
  LogoNuriError,
  fetchLogoNuriAsset,
  listLogoNuriKits
} from '../../templates/logonuri-api.js';
import { KIT_ASSET_KEYS, normalizeBrandKit } from '../../shared/brand-kit-plan.js';

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;
const FILENAME_RE = /^[A-Za-z0-9._-]{1,64}$/;

/** Human labels for the keys a kit can publish. */
const ASSET_LABELS = Object.freeze({
  logo: 'Logo (vector)',
  logo_transparent: 'Logo (transparent)',
  logo_white_bg: 'Logo on white',
  logo_dark_bg: 'Logo on dark',
  favicon_32x32: 'Favicon',
  og_blank: 'Social card',
  qr_code: 'QR code'
});

/**
 * Keys the GRID offers — PICKABLE, which is a wider claim than PLACEABLE.
 *
 * `KIT_ASSET_KEYS` stays exactly the six keys the APPLY places, and must not
 * be widened to match this list: it also gates `normalizeBrandKitState`, which
 * is what a project PERSISTS in `state.brandKit.assets` — and `qr_code` is an
 * image someone inserts into a section, not part of the brand-apply contract.
 * The turntable is deliberately in NEITHER list: the proxy refuses the key
 * (no Range support), so a grid card for it would be an affordance offered
 * where it cannot act. It previews in the kit header instead, straight from
 * the public origin.
 */
export const PICKABLE_KIT_KEYS = Object.freeze([...KIT_ASSET_KEYS, 'qr_code']);

/**
 * The three assets an APP ICON may come from.
 *
 * Narrower than the full set on purpose, and each exclusion is a real failure
 * rather than a preference: the SVG is rejected outright by
 * `validatePwaImageFile`, `favicon_32x32` is guaranteed to fail the ≥512 check
 * (offering-then-rejecting is worse than not offering), and `og_blank` is not
 * square. What IS left is the genuine decision — which background.
 */
export const APP_ICON_KEYS = Object.freeze(['logo_transparent', 'logo_white_bg', 'logo_dark_bg']);

/**
 * The assets a SOCIAL CARD slot may come from. Exactly one today, and the
 * single-entry list is the point: `og_blank` is the only kit asset with the
 * 1.91:1 proportion a social card needs, and offering a 1024² logo for an
 * `og:image` produces a card that every scraper letterboxes or crops.
 */
export const OG_KEYS = Object.freeze(['og_blank']);

/**
 * The assets a LOGO slot may come from — every mark, no favicon and no social
 * card. `favicon_32x32` is excluded for the reason it is excluded from the app
 * icon: 32px placed in a nav is a visibly broken logo, not a small one.
 */
export const LOGO_KEYS = Object.freeze([
  'logo',
  'logo_transparent',
  'logo_white_bg',
  'logo_dark_bg'
]);

const PURPOSE_KEYS = Object.freeze({
  'app-icon': APP_ICON_KEYS,
  og: OG_KEYS,
  logo: LOGO_KEYS
});

/**
 * Prefix for a FOLDER record's id.
 *
 * Folder records and asset records ride the same `search()` return and the same
 * grid, so the two have to be told apart at the pick boundary — activating a
 * folder must open it, never resolve the picker with an image. The
 * discriminator is `_kitFolder` on the record; this prefix exists so the ids
 * cannot collide with `logonuri-<slug>-<publicKey>` either.
 */
const FOLDER_ID_PREFIX = 'logonuri-kit-';

let purpose = null;
let cachedKits = null;

/** Narrow the grid for a caller with a specific slot to fill (LN-7). */
export function setKitAssetPurpose(next) {
  if (purpose !== next) cachedKits = null;
  purpose = next && PURPOSE_KEYS[next] ? next : null;
}

/** Test seam, and what the picker calls when it closes. */
export function resetKitAdapterState() {
  purpose = null;
  cachedKits = null;
}

/**
 * Seed the kit cache without a network call.
 *
 * Exported for tests only, and deliberately the SAME field `loadKits` fills, so
 * a test drives the real `search`/`getCategories`/`itemsForKit` path rather
 * than a parallel one that could pass while the real one is broken.
 */
export function __setKitsForTests(kits) {
  cachedKits = Array.isArray(kits) ? kits : null;
}

function keysForPurpose() {
  return purpose ? PURPOSE_KEYS[purpose] : PICKABLE_KIT_KEYS;
}

/**
 * A kit asset's PUBLIC thumbnail URL.
 *
 * Both inputs are re-validated here even though the Worker already validated
 * them: "the server checked it" stops being true the moment a response is
 * cached, replayed, or hand-edited in a fixture. A rejected input yields no
 * thumbnail rather than an unchecked URL.
 */
export function kitThumbUrl(slug, filename) {
  if (!SLUG_RE.test(String(slug || ''))) return '';
  if (!FILENAME_RE.test(String(filename || ''))) return '';
  return `${LOGONURI_ORIGIN}/api/brand/${slug}/asset/${filename}`;
}

async function loadKits({ refresh = false } = {}) {
  if (cachedKits && !refresh) return cachedKits;
  try {
    const { kits } = await listLogoNuriKits({ refresh });
    cachedKits = Array.isArray(kits) ? kits : [];
    return cachedKits;
  } catch (err) {
    // The picker has no async gate hook, so a link problem arrives as a coded
    // SEARCH failure and is rendered by the existing gate-prompt path.
    if (err instanceof LogoNuriError) {
      // `logonuri_link_expired` is LN-2's mapping of an upstream 401 — the row
      // has already been cleared, so from here it is indistinguishable from
      // never having connected, and the remedy is the same button.
      if (
        err.code === 'not_connected' ||
        err.code === 'logonuri_link_expired' ||
        err.status === 409
      ) {
        throw Object.assign(new Error('Not connected'), { code: 'LOGONURI_NOT_CONNECTED' });
      }
      if (err.code === 'logonuri_pro_required' || err.status === 403) {
        throw Object.assign(new Error('LogoNuri Pro required'), {
          code: 'LOGONURI_PRO_REQUIRED',
          upgradeUrl: err.upgradeUrl
        });
      }
    }
    throw err;
  }
}

/**
 * The mark a FOLDER shows, in preference order.
 *
 * Transparent PNG first, not the SVG: the folder poster sits on the
 * checkerboard, which is what makes "this mark has no background" legible at
 * thumbnail size — and a transparent PNG is the variant guaranteed to have
 * been generated at 1024². The vector is the better asset and the worse
 * thumbnail.
 */
const FOLDER_MARK_KEYS = Object.freeze([
  'logo_transparent',
  'logo',
  'logo_white_bg',
  'logo_dark_bg',
  'favicon_32x32'
]);

function folderMarkFor(kit) {
  const assets = kit?.assets || [];
  for (const key of FOLDER_MARK_KEYS) {
    const asset = assets.find((entry) => entry?.publicKey === key);
    if (asset?.filename) return asset;
  }
  return null;
}

/**
 * One kit as a FOLDER record.
 *
 * A record and not a `tiles` entry, deliberately. A tile is the picker's shape
 * for something that is not a record — upload, blank, paste — and it is
 * excluded from `_entries`, from selection, from the filter pass and from the
 * counts. A kit is none of those things: it is a browsable row of the list the
 * user is searching, and making it a record is what gets it filtering, roving
 * keyboard focus and the entrance stagger for free.
 */
function folderForKit(kit) {
  const mark = folderMarkFor(kit);
  const count = (kit?.assets || []).length;
  return {
    id: `${FOLDER_ID_PREFIX}${kit.slug}`,
    thumb: mark ? kitThumbUrl(kit.slug, mark.filename) : '',
    title: kit.brandName || kit.slug,
    author: '',
    authorUrl: '',
    variantCount: 1,
    variants: [],
    _kitFolder: true,
    _kit: { slug: kit.slug, brandName: kit.brandName, assetRevision: kit.assetRevision },
    // Read by the picker to build the folder's facts line and palette strip.
    // Carried on the record rather than re-fetched, so the grid never waits on
    // a second call to render a row it already has the data for.
    _folder: {
      slug: kit.slug,
      brandName: kit.brandName || kit.slug,
      assetCount: count,
      version: Number.isInteger(kit.version) ? kit.version : null,
      updatedAt: typeof kit.updatedAt === 'string' ? kit.updatedAt : null,
      palette: (kit.palette || []).slice(0, 6).map((entry) => entry.hex),
      keys: (kit.assets || []).map((asset) => asset.publicKey)
    }
  };
}

/** Is this record (or picker item payload) a kit folder rather than an asset? */
export function isKitFolder(record) {
  return record?._kitFolder === true;
}

/** The slug a folder record opens, or '' for anything else. */
export function kitFolderSlug(record) {
  return isKitFolder(record) ? String(record?._kit?.slug || '') : '';
}

/**
 * The kit for a slug as ONE shape, from the cache the grid was rendered from.
 *
 * THERE ARE TWO KIT SHAPES IN THIS FEATURE and this function is where they stop
 * being two. The Worker sends `assets` as an ARRAY of descriptors (what
 * `itemsForKit` and `folderForKit` iterate); `normalizeBrandKit` turns it into
 * a keyed MAP (what the planner, the apply engine and the header card index
 * into). Handing the raw shape to a consumer that expects the map fails
 * silently as "this kit fills nothing" rather than loudly, so the conversion
 * happens once, here, and `markThumb` is folded in so no caller has to know the
 * mark-preference order either.
 *
 * `planBrandKitApply` accepts an already-normalized kit as-is, so the object
 * returned here is equally the display model and the apply input — one object,
 * no chance of the card describing a different kit than the button applies.
 *
 * Synchronous and cache-only on purpose: its caller is the header card, which
 * paints during a render pass. Returning `null` on a cold cache is correct —
 * the header simply does not paint that frame, and the render triggered by the
 * search that fills the cache has it.
 */
export function getCachedKitView(slug) {
  if (!cachedKits) return null;
  const raw = cachedKits.find((kit) => kit.slug === slug);
  if (!raw) return null;
  const kit = normalizeBrandKit(raw);
  if (!kit) return null;
  const mark = folderMarkFor(raw);

  // The turntable PREVIEW url — the one asset that never touches the proxy.
  // `kitThumbUrl` builds it from the constant origin plus a re-validated slug
  // and filename, exactly as it builds every thumbnail: LogoNuri's asset route
  // is public with CORS `*` and serves Range itself, and a preview that fails
  // to build (validation refused something) is simply absent rather than a
  // broken player.
  const turntableAsset = (raw.assets || []).find((a) => a?.publicKey === 'logo_turntable');
  const turntableUrl = turntableAsset ? kitThumbUrl(slug, turntableAsset.filename) : '';
  const turntable = turntableUrl
    ? { url: turntableUrl, contentType: String(turntableAsset.contentType || '') }
    : null;

  return {
    ...kit,
    markThumb: mark ? kitThumbUrl(slug, mark.filename) : '',
    // The RAW listing's count, not the normalized map's: `normalizeBrandKit`
    // keeps only the PLACEABLE keys, so a kit with a QR code and a turntable
    // would read "6 assets" above a grid showing seven cards and a video.
    assetCount: (raw.assets || []).length,
    qr: raw.qr || null,
    turntable
  };
}

function itemsForKit(kit) {
  const assets = kit?.assets || [];
  const wanted = keysForPurpose();
  return assets
    .filter((asset) => wanted.includes(asset?.publicKey))
    .map((asset) => ({
      id: `logonuri-${kit.slug}-${asset.publicKey}`,
      thumb: kitThumbUrl(kit.slug, asset.filename),
      title: ASSET_LABELS[asset.publicKey] || asset.publicKey,
      author: kit.brandName || kit.slug,
      authorUrl: '',
      variantCount: 1,
      variants: [],
      _kit: { slug: kit.slug, brandName: kit.brandName, assetRevision: kit.assetRevision },
      _publicKey: asset.publicKey,
      // The QR card's disclosure rides the record: the mode is a fact about
      // THIS asset (a dynamic code stays repointable after an export ships),
      // and the session's adapt() patches the card's meta line from it.
      ...(asset.publicKey === 'qr_code' ? { _qr: kit.qr || null } : {})
    }));
}

export const logoNuriKits = {
  name: 'Brand kits',
  needsKey: false,
  lsKey: null,
  attribution: 'Brand assets from your LogoNuri account',
  keyHelp: '',

  // The Local tab browses with no query; so does this one. The flag exists so
  // the picker stops hardcoding `activeKey === 'local'` for that behaviour.
  browseWithoutQuery: true,
  browseHint: 'Pick a brand kit, or search by asset name.',

  // Deliberately NO `getSourceUrl` — see the header. Do not add one.

  getApiKey() {
    return '';
  },
  setApiKey() {},

  /** Kits, rendered into the picker's existing category `<select>`. */
  async getCategories() {
    const kits = await loadKits();
    return kits.map((kit) => ({ value: kit.slug, label: kit.brandName || kit.slug }));
  },

  /**
   * Two levels, decided by `category` and `query` — never by a mode flag.
   *
   *   no kit, no query  → the FOLDERS. One record per kit.
   *   no kit, a query   → assets ACROSS every kit. Searching "favicon" from the
   *                       folder screen must find favicons, not folders; the
   *                       browse hint promises exactly that, and folding the
   *                       query into a folder-name match would break it.
   *   a kit             → that kit's assets, narrowed by any active purpose.
   *
   * A query never matches folders, so there is no state in which the two record
   * shapes are interleaved — which is what keeps the pick boundary in
   * `onActivate` a single `isKitFolder` test rather than a per-item guess.
   */
  async search(query, page, _signal, { category = '' } = {}) {
    const kits = await loadKits();
    const q = String(query || '')
      .trim()
      .toLowerCase();

    if (!category && !q) {
      const folders = kits.map(folderForKit);
      return { results: page === 1 ? folders : [], total: folders.length };
    }

    const scoped = category ? kits.filter((kit) => kit.slug === category) : kits;
    let items = scoped.flatMap(itemsForKit);
    if (q) {
      items = items.filter((item) => `${item.title} ${item.author}`.toLowerCase().includes(q));
    }
    // Every kit's assets fit in one page; paginating six rows would be theatre.
    return { results: page === 1 ? items : [], total: items.length };
  },

  async download(item) {
    const kit = item?._kit;
    // A folder is not downloadable and never reaches here — `onActivate` opens
    // it instead of picking it. Stated anyway, because the failure without this
    // is the generic "no longer available" below, which would send someone
    // looking for a revoked asset rather than at the routing bug they have.
    if (isKitFolder(item)) {
      throw Object.assign(new Error('A brand kit is a folder, not an image.'), {
        code: 'INVALID_URL'
      });
    }
    if (!kit?.slug || !item?._publicKey) {
      throw Object.assign(new Error('That brand asset is no longer available.'), {
        code: 'INVALID_URL'
      });
    }
    return fetchLogoNuriAsset(kit.slug, item._publicKey, { revision: kit.assetRevision });
  },

  /** What the status line says for a failure this source can produce. */
  describeError(err) {
    if (err?.code === 'LOGONURI_NOT_CONNECTED') return '';
    if (err?.code === 'LOGONURI_PRO_REQUIRED') return '';
    if (err?.code === 'logonuri_unconfigured') {
      return 'Brand kits are temporarily unavailable. Try again in a moment.';
    }
    if (err?.code === 'logonuri_rate_limited') return 'Too many requests. Try again shortly.';
    return '';
  },

  /**
   * The gate a hard failure ends in.
   *
   * Every branch finishes in a BUTTON that fixes the problem — connect, or see
   * the plans — rather than a dead-end sentence telling the user something is
   * wrong and leaving them there.
   */
  gateFor(err) {
    if (err?.code === 'LOGONURI_NOT_CONNECTED') {
      return {
        title: 'Connect your LogoNuri account',
        help: 'Link LogoNuri once and every brand kit you own shows up here.',
        actionLabel: 'Connect LogoNuri',
        action: 'connect'
      };
    }
    if (err?.code === 'LOGONURI_PRO_REQUIRED') {
      return {
        title: 'LogoNuri Pro is required',
        help: 'Brand kits are part of LogoNuri Pro. Your account is on the free plan.',
        actionLabel: 'See LogoNuri plans',
        action: 'upgrade',
        upgradeUrl: err.upgradeUrl
      };
    }
    return null;
  },

  emptyState({ query, category }) {
    if (query) {
      return {
        title: `No brand assets match “${query}”`,
        hint: 'Try a different word, or pick another kit.'
      };
    }
    if (category) {
      return { title: 'That kit has no assets yet.', hint: 'Publish it in LogoNuri first.' };
    }
    return {
      title: 'No brand kits yet.',
      hint: 'Create one in LogoNuri and it will appear here.'
    };
  }
};
